import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Constants from 'expo-constants';
import { View, StyleSheet, ScrollView, PixelRatio } from 'react-native';
import { Banner, useTheme } from 'react-native-paper';
import { NavigationContext } from '@react-navigation/native';
import Typography from '../../components/Typography';
import {
  NOTIFICATION_LIFE_TIME_MS,
  RESERVED_NOTIFICATION_KEYS,
  useMapViewBanner,
} from '../../context/MapViewBannerContext';
import { useBitwards } from '../../bitwards/BitwardsContext';
import { flattenDict } from '../../utils/helpers';
import { setNotificationListener } from '../../utils/pushNotifications';
import { screenNames } from '../../navigators/screenNames';
import { useI18n } from '../../context/I18nContext';
import useScreenSize from '../../hooks/useScreenSize';
import { useNowMinutes } from '../../hooks/useNow';
import { useMixpanel } from '../../mixpanel/MixpanelContext';
import { SelectResourceModal } from '../../components/modal/SelectResourceModal';
import { ProductSubscription, ProductSubscriptionStatus, Resource, ServiceType } from '../../types/appsync-types';
import { useMyProductReservations } from '../../apis/subscriptionApis';
import { useMapViewFilter } from '../../context/MapViewFilterContext';

type NotificationBannerProps = {
  onBannerHeightChange?: (visibleHeight: number) => void;
};

export function NotificationBanner({ onBannerHeightChange }: NotificationBannerProps) {
  const { colors } = useTheme();
  const { windowHeight } = useScreenSize();
  const mp = useMixpanel();
  const { timeZone } = useMapViewFilter();
  const now = useNowMinutes();
  const { currentNotificationPayload, removeNotificationWithKey, displayNotification } = useMapViewBanner();
  const { I18n, formatDate } = useI18n();
  const navigation = useContext(NavigationContext);
  const { accessResource, loading } = useBitwards();
  const [modalResources, setModalResources] = useState<(Resource | undefined | null)[] | null>(null);

  const { ongoingReservations, unpaidReservations, upcomingReservations, rest } = useMyProductReservations();

  const reservationCount = useMemo(
    () => ongoingReservations.length + unpaidReservations.length + upcomingReservations.length + rest.length,
    [ongoingReservations, unpaidReservations, upcomingReservations, rest],
  );

  const getOtherStatusText = useCallback(
    (reservation: ProductSubscription) => {
      const translationKeyPrefix = reservation.product?.serviceType === ServiceType.LOCKER ? 'lockerInfo.' : '';
      switch (reservation?.status) {
        case ProductSubscriptionStatus.CANCELLING:
          return I18n.t(translationKeyPrefix + 'mapview.podInfoCard.cancellingTitle');
        case ProductSubscriptionStatus.REQUIRES_ACTION:
        case ProductSubscriptionStatus.ENDED_NOT_PAID:
          return I18n.t(translationKeyPrefix + 'mapview.podInfoCard.unpaidTitle');
        case ProductSubscriptionStatus.FAILED:
          return I18n.t(translationKeyPrefix + 'mapview.podInfoCard.failedTitle');
        case ProductSubscriptionStatus.PROCESSING:
        default:
          return I18n.t(translationKeyPrefix + 'mapview.podInfoCard.processingTitle');
      }
    },
    [I18n],
  );

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (currentNotificationPayload) {
      setTimeout(() => {
        displayNotification(null);
      }, NOTIFICATION_LIFE_TIME_MS);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [currentNotificationPayload, displayNotification]);

  useEffect(() => {
    if (reservationCount) {
      removeNotificationWithKey(RESERVED_NOTIFICATION_KEYS.BOOKING_SUCCEED);
    }
  }, [reservationCount, removeNotificationWithKey]);

  useEffect(() => {
    // get the notification data when notification is received
    setNotificationListener((notification: any) => {
      // Note that the notification object structure is different from Android and IOS
      const maybeReservationId = notification?._data?.data?.jsonBody?.reservationId;
      if (maybeReservationId) {
        navigation?.navigate('map', {
          screen: screenNames.BookingDetail,
          params: {
            bookingId: maybeReservationId,
          },
        });
      }

      // TODO: Add news related push note navigation here
    });
  }, [navigation]);

  const content = (() => {
    return (
      <View>
        <Typography variant={'sub1'}>{currentNotificationPayload?.helperContent}</Typography>
        <Typography variant={'h6'}>{currentNotificationPayload?.mainContent}</Typography>
      </View>
    );
  })();

  const actions = useMemo(() => {
    const ret = [];
    if (reservationCount === 1) {
      const ongoingReservation = ongoingReservations[0];
      const upcomingReservation = upcomingReservations[0];
      const unpaidReservation = unpaidReservations[0];
      const restReservation = rest[0];

      if (ongoingReservation) {
        if (!Constants.manifest?.extra?.disableBitwards && ongoingReservation?.product?.linkedPods?.length === 1) {
          const space = ongoingReservation.product.linkedPods[0];
          ret.push({
            label: loading ? I18n.t('mapview.banner.unlocking') : I18n.t('mapview.banner.unlock'),
            onPress: () => {
              if (!loading) {
                mp?.track('Unlock attempt from banner', flattenDict('space', space));
                if (space?.resources?.length === 1 && space.resources[0]?.resourceId) {
                  accessResource(space.resources[0]?.resourceId);
                } else if (space?.resources && space.resources.length > 1) {
                  setModalResources(space.resources);
                }
              }
            },
          });
        }
        ret.push({
          label: I18n.t(
            (ongoingReservation.product?.serviceType === ServiceType.LOCKER ? 'lockerInfo.' : '') +
              'mapview.banner.endReservation',
          ),
          onPress: () => {
            navigation?.navigate(screenNames.ConfirmReservationProductEndDialog, { id: ongoingReservation.id });
          },
        });
      }

      ret.push({
        label: unpaidReservation ? I18n.t('mapview.banner.pay') : I18n.t('mapview.banner.viewDetails'),
        onPress: () => {
          navigation?.navigate(screenNames.ReservationProductDetailScreen, {
            productId: (upcomingReservation ?? ongoingReservation ?? unpaidReservation ?? restReservation)?.productId,
          });
        },
      });
    }
    if (reservationCount > 1) {
      ret.push({
        label: I18n.t('mapview.banner.viewDetails'),
        onPress: () => {
          navigation?.navigate(screenNames.OneTimePurchaseProductsScreen, {
            filter: 'active',
          });
        },
      });
    }

    return ret;
  }, [
    I18n,
    accessResource,
    loading,
    mp,
    navigation,
    reservationCount,
    ongoingReservations,
    upcomingReservations,
    unpaidReservations,
    rest,
  ]);

  return (
    <ScrollView
      style={[styles.wrapper, { top: 0 }, PixelRatio.getFontScale() > 1.5 ? { maxHeight: windowHeight / 3 } : {}]}
      onLayout={(event) => {
        onBannerHeightChange?.(event.nativeEvent.layout.height);
      }}
      bounces={false}
    >
      {/* To avoid race condition in showing current reservation banner, it should be handled in
          a separate instance than normal notification banner. This banner will take lower priority
          than normal banner and will be hidden when there is an active notification payload
       */}
      {reservationCount === 1 && ongoingReservations[0] && (
        <Banner actions={actions} visible={!currentNotificationPayload}>
          {
            // Cast to any, as Banner doesn't support non text children accorind to TS
            (
              <View>
                <Typography variant={'sub1'}>
                  {I18n.t(
                    (ongoingReservations[0].product?.serviceType === ServiceType.LOCKER ? 'lockerInfo.' : '') +
                      'mapview.banner.reservationOngoing',
                  )}
                </Typography>
                <Typography variant={'h6'}>
                  {I18n.t('mapview.banner.since', {
                    time: formatDate(new Date(ongoingReservations[0].startDate ?? now), 'timeAndDate', timeZone),
                  })}
                </Typography>
                <Typography variant={'sub1'} color={colors.primary} style={{ fontWeight: 'bold' }}>
                  {ongoingReservations[0].product?.name}
                </Typography>
              </View>
            ) as any
          }
        </Banner>
      )}
      {reservationCount === 1 && unpaidReservations[0] && (
        <Banner actions={actions} visible={!currentNotificationPayload}>
          {
            // Cast to any, as Banner doesn't support non text children accorind to TS
            (
              <View>
                <Typography variant={'sub1'}>
                  {I18n.t(
                    (unpaidReservations[0].product?.serviceType === ServiceType.LOCKER ? 'lockerInfo.' : '') +
                      'mapview.banner.reservationUnpaid',
                  )}
                </Typography>
                <Typography variant={'sub1'} color={colors.primary} style={{ fontWeight: 'bold' }}>
                  {unpaidReservations[0].product?.name}
                </Typography>
              </View>
            ) as any
          }
        </Banner>
      )}
      {reservationCount === 1 && upcomingReservations[0] && (
        <Banner actions={actions} visible={!currentNotificationPayload}>
          {
            // Cast to any, as Banner doesn't support non text children accorind to TS
            (
              <View>
                <Typography variant={'sub1'}>
                  {I18n.t(
                    (upcomingReservations[0].product?.serviceType === ServiceType.LOCKER ? 'lockerInfo.' : '') +
                      'mapview.banner.reservationUpcoming',
                  )}
                </Typography>
                <Typography variant={'h6'}>
                  {formatDate(new Date(upcomingReservations[0].startDate ?? now), 'timeAndDate', timeZone)}
                </Typography>
                <Typography variant={'sub1'} color={colors.primary} style={{ fontWeight: 'bold' }}>
                  {upcomingReservations[0].product?.name}
                </Typography>
              </View>
            ) as any
          }
        </Banner>
      )}
      {reservationCount === 1 && rest[0] && (
        <Banner actions={actions} visible={!currentNotificationPayload}>
          {
            // Cast to any, as Banner doesn't support non text children accorind to TS
            (
              <View>
                <Typography variant={'sub1'}>{getOtherStatusText(rest[0])}</Typography>
                <Typography variant={'sub1'}>{rest[0].product?.name}</Typography>
              </View>
            ) as any
          }
        </Banner>
      )}
      {reservationCount > 1 && (
        <Banner actions={actions} visible={!currentNotificationPayload}>
          {
            // Cast to any, as Banner doesn't support non text children accorind to TS
            (
              <View>
                <Typography variant={'sub1'}>{I18n.t('mapview.banner.multipleReservations')}</Typography>
                <Typography variant={'sub2'}>
                  {I18n.t('mapview.banner.reservationStatuses', {
                    active: ongoingReservations.length,
                    unpaid: unpaidReservations.length,
                    upcoming: upcomingReservations.length,
                    other: rest.length,
                  })}
                </Typography>
              </View>
            ) as any
          }
        </Banner>
      )}
      <Banner
        actions={currentNotificationPayload?.actions ?? []}
        visible={!!currentNotificationPayload}
        icon={currentNotificationPayload?.icon}
      >
        {/* Banner children TS definition only allow text, even though normal react component works
            This is a workaround and in near future there is no reliable and not hacky fix
         */}
        {content as any}
      </Banner>
      {!!modalResources && (
        <SelectResourceModal
          resources={modalResources}
          isVisible={!!modalResources}
          loading={loading}
          onDismiss={() => setModalResources(null)}
        />
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
  },
});
